import React from 'react';
import { Offer } from '../types';
import { staticImage } from '../constants';
import { getWineColorClass, getWarehouseFlag, buildWineUrls } from '../utils';
import { useTranslation } from '../hooks/useTranslation';

interface OfferCardProps {
  offer: Offer;
  isBestOffer: boolean;
  currency?: string;
  language?: string;
  user?: any;
  onCardClick: (buyNowUrl: string) => void;
  onInfoClick: (e: React.MouseEvent, moreInfoUrl: string) => void;
  onShareClick: (e: React.MouseEvent, offer: Offer, moreInfoUrl: string) => void;
}

const OfferCard: React.FC<OfferCardProps> = React.memo(({ 
  offer, 
  isBestOffer, 
  currency, 
  language,
  user,
  onCardClick, 
  onInfoClick, 
  onShareClick 
}) => {
  const { t } = useTranslation(language);
  const colorClass = getWineColorClass(offer.color);
  const { moreInfoUrl, buyNowUrl } = buildWineUrls(offer, user);
  const flag = getWarehouseFlag(offer.warehouse_name);
  const produceAndName = offer.display_name?.split(',') || [offer.processed_wine_name];

  return (
    <div 
      className={`offer-card ${isBestOffer ? 'best-offer' : ''}`}
      onClick={() => onCardClick(buyNowUrl)}
    >
      {isBestOffer && <div className="best-offer-ribbon"></div>}
      
      <div className="offer-action-icons">
        <div 
          className="action-icon info-icon" 
          onClick={(e) => onInfoClick(e, moreInfoUrl)}
          title={t('moreInformation')}
        >
          <i className="fas fa-info-circle"></i>
        </div>
        <div 
          className="action-icon share-icon" 
          onClick={(e) => onShareClick(e, offer, moreInfoUrl)}
          title={t('share')}
        >
          <i className="fas fa-paper-plane"></i>
        </div>
      </div>

      <div className="offer-image-container">
        <img 
          src={offer.image_url || staticImage('logo.png')} 
          alt={offer.display_name || 'Wine bottle'} 
          onError={(e) => { 
            const target = e.target as HTMLImageElement;
            target.src = staticImage('logo.png'); 
          }}
        />
      </div>

      <div className="offer-details">
        <i className={`fas fa-wine-bottle wine-type-icon ${colorClass}`}></i>
        
        <div className="offer-vintage-name">
          <span className="offer-vintage">{offer.vintage || 'NV'}</span>
          {produceAndName.length > 1 && (
            <span className="offer-producer">{produceAndName[0]}</span>
          )}
          <span className="offer-name">
            {produceAndName.length > 1 ? produceAndName[1] : produceAndName[0]}
          </span>
        </div>

        {offer.ratings_and_notes?.rating && (
          <span className="offer-rating-container">
            <span className="rating-label">WA</span>
            <span className="rating-value">{offer.ratings_and_notes.rating}</span>
          </span>
        )}

        <div className="offer-price-buttons">
          <div className="offer-price">
            <div className="offer-case-format">
              {offer.quantity_per_case || '?'} x {offer.volume || '?'} ({offer.quantity} qty)
            </div>
            <div className="offer-price-target">
              {offer.per_bottle_price 
                ? `${offer.calculated_currency || currency} ${offer.per_bottle_price.toLocaleString()}` 
                : t('priceNA')
              }
              <span className="price-label">{offer.volume ? `per ${offer.volume}` : t('perBottle')}</span>
            </div>
          </div>

          <div className="offer-buttons">
            {offer.original_currency && offer.original_price && (
              <div className="offer-price-original">
                {offer.original_currency} {Math.round(offer.original_price).toLocaleString()}
                <div className="price-label">
                  {offer.quantity_per_case || '?'} x {offer.volume || '?'}
                </div>
              </div>
            )}
            <a href={buyNowUrl} target="_blank" className="buy-now-button" rel="noopener noreferrer">
              {t('buyNow')}
            </a>
          </div>

          {flag.filename && (
            <div className="warehouse-flag-container">
              <img 
                src={staticImage(flag.filename)} 
                alt={`${offer.warehouse_name} Flag`} 
                className="warehouse-flag" 
              />
              <span className="warehouse-flag-text">{flag.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

OfferCard.displayName = 'OfferCard';

export default OfferCard;
